import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, ArrowUp, ArrowDown, Upload as UploadIcon } from "@phosphor-icons/react";

const STATUSES = ["pending", "confirmed", "active", "completed", "cancelled"];
const STATUS_COLOR = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  active: "bg-green-100 text-green-800",
  completed: "bg-neutral-200 text-neutral-700",
  cancelled: "bg-red-100 text-red-700",
};
const ICONS = ["wrench", "lightning", "gear", "battery", "wheel", "check"];

const emptyService = () => ({
  name: "", key: "", category: "maintenance",
  short_description: "", description: "",
  price: 0, price_from: true, duration: "",
  icon: "wrench", image: "",
  active: true,
});

export default function AdminServices() {
  const [tab, setTab] = useState("catalog"); // catalog | requests
  const [items, setItems] = useState([]);      // catalog
  const [requests, setRequests] = useState([]); // bookings
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyService());
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/services").then((r) => setItems(r.data)),
      api.get("/admin/service-bookings").then((r) => setRequests(r.data)),
    ]).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updateRequestStatus = async (b, status) => {
    try {
      await api.patch(`/admin/service-bookings/${b.id}`, { status });
      toast.success(`→ ${status}`); load();
    } catch { toast.error("Update failed"); }
  };

  const openNew = () => { setForm(emptyService()); setEditing("new"); };
  const openEdit = (svc) => { setForm({ ...emptyService(), ...svc }); setEditing(svc.id); };
  const closeEditor = () => { setEditing(null); setForm(emptyService()); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/services", form);
        toast.success("Service created");
      } else {
        await api.put(`/admin/services/${editing}`, form);
        toast.success("Service updated");
      }
      closeEditor(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/services/${svc.id}`);
      toast.success("Deleted"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (svc) => {
    try {
      await api.put(`/admin/services/${svc.id}`, { ...svc, active: !svc.active });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const move = async (svc, direction) => {
    const idx = items.findIndex((s) => s.id === svc.id);
    const nextIdx = direction === "up" ? idx - 1 : idx + 1;
    if (nextIdx < 0 || nextIdx >= items.length) return;
    const newOrder = [...items];
    [newOrder[idx], newOrder[nextIdx]] = [newOrder[nextIdx], newOrder[idx]];
    setItems(newOrder);
    try {
      await api.post("/admin/services/reorder", { ids: newOrder.map((s) => s.id) });
    } catch { toast.error("Reorder failed"); load(); }
  };

  const TAB_CLS = (k) => `px-4 py-2 text-xs uppercase font-bold tracking-widest border-b-2 transition-colors ${tab === k ? "border-[color:var(--vt-orange)] text-black" : "border-transparent text-neutral-500 hover:text-black"}`;

  return (
    <div className="p-8 lg:p-12" data-testid="admin-services">
      <div className="mb-6">
        <div className="overline text-[color:var(--vt-orange)] mb-2">/ WORKSHOP</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Services</h1>
      </div>

      <div className="flex gap-1 border-b border-neutral-200 mb-6">
        <button onClick={() => setTab("catalog")} className={TAB_CLS("catalog")} data-testid="tab-catalog">Catalog ({items.length})</button>
        <button onClick={() => setTab("requests")} className={TAB_CLS("requests")} data-testid="tab-requests">Service Requests ({requests.length})</button>
      </div>

      {loading && <div className="p-12 text-center text-neutral-500">Loading…</div>}

      {!loading && tab === "catalog" && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-neutral-500">{items.length} service{items.length === 1 ? "" : "s"} · use ↑↓ to reorder · inactive services are hidden from the public workshop page</p>
            <button onClick={openNew} className="vt-btn-orange text-sm" data-testid="add-service-btn">
              <Plus size={16} /> Add service
            </button>
          </div>
          {items.length === 0 ? (
            <div className="p-12 text-center text-neutral-500 border border-neutral-200 bg-white">No services yet. Click "Add service" to get started.</div>
          ) : (
            <div className="bg-white border border-neutral-200" data-testid="service-catalog-table">
              <table className="w-full text-sm">
                <thead className="bg-neutral-50 border-b border-neutral-200">
                  <tr>
                    <th className="text-left p-3 overline text-neutral-600 w-24">Order</th>
                    <th className="text-left p-3 overline text-neutral-600">Service</th>
                    <th className="text-left p-3 overline text-neutral-600">Category</th>
                    <th className="text-right p-3 overline text-neutral-600">Price</th>
                    <th className="text-left p-3 overline text-neutral-600">Duration</th>
                    <th className="text-center p-3 overline text-neutral-600">Active</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((svc, idx) => (
                    <tr key={svc.id} className={`border-b border-neutral-100 hover:bg-neutral-50 ${!svc.active ? "opacity-50" : ""}`} data-testid={`service-row-${svc.key}`}>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <button onClick={() => move(svc, "up")} disabled={idx === 0} className="p-1 border border-neutral-300 hover:border-black disabled:opacity-30"><ArrowUp size={10} /></button>
                          <button onClick={() => move(svc, "down")} disabled={idx === items.length - 1} className="p-1 border border-neutral-300 hover:border-black disabled:opacity-30"><ArrowDown size={10} /></button>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="font-bold">{svc.name}</div>
                        <div className="text-[10px] text-neutral-500 truncate max-w-xs">{svc.short_description}</div>
                      </td>
                      <td className="p-3 text-xs uppercase text-neutral-600">{svc.category}</td>
                      <td className="p-3 text-right font-bold text-[color:var(--vt-orange-hover)]">{svc.price_from ? "from " : ""}€{svc.price?.toFixed(2)}</td>
                      <td className="p-3 text-xs text-neutral-600">{svc.duration || "—"}</td>
                      <td className="p-3 text-center">
                        <label className="inline-flex items-center cursor-pointer">
                          <input type="checkbox" checked={!!svc.active} onChange={() => toggleActive(svc)} data-testid={`toggle-${svc.key}`} />
                        </label>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => openEdit(svc)} className="p-2 border border-black hover:bg-black hover:text-white transition-colors" title="Edit" data-testid={`edit-${svc.key}`}><PencilSimple size={12} /></button>
                          <button onClick={() => remove(svc)} className="p-2 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors" title="Delete" data-testid={`delete-${svc.key}`}><Trash size={12} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {!loading && tab === "requests" && (
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          {requests.length === 0 ? <div className="p-12 text-center text-neutral-500">No service requests yet.</div> :
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left p-3 overline text-neutral-600">Service Type</th>
                <th className="text-left p-3 overline text-neutral-600">Customer</th>
                <th className="text-left p-3 overline text-neutral-600">Bike</th>
                <th className="text-left p-3 overline text-neutral-600">Date</th>
                <th className="text-left p-3 overline text-neutral-600">Contact</th>
                <th className="text-left p-3 overline text-neutral-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((b) => (
                <tr key={b.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                  <td className="p-3 font-bold">{b.service_type}</td>
                  <td className="p-3">{b.customer_name}</td>
                  <td className="p-3 text-xs">{b.bike_details || "—"}</td>
                  <td className="p-3 text-xs">{b.preferred_date}</td>
                  <td className="p-3 text-xs">
                    <div>{b.customer_email}</div>
                    <div>{b.customer_phone}</div>
                  </td>
                  <td className="p-3">
                    <select className={`text-xs px-2 py-1 font-bold uppercase ${STATUS_COLOR[b.status] || ""}`}
                      value={b.status} onChange={(e) => updateRequestStatus(b, e.target.value)}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>}
        </div>
      )}

      {editing && (
        <ServiceEditor
          form={form} setForm={setForm}
          onCancel={closeEditor} onSubmit={submit}
          saving={saving} isNew={editing === "new"}
        />
      )}
    </div>
  );
}

// ------- Editor modal -------
function ServiceEditor({ form, setForm, onCancel, onSubmit, saving, isNew }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileInput.current) fileInput.current.value = ""; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onCancel} data-testid="service-editor-modal">
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full p-6 my-8 relative">
        <button type="button" onClick={onCancel} className="absolute top-3 right-3 p-2 hover:bg-neutral-100" data-testid="service-editor-close"><X size={20} /></button>
        <div className="overline text-[color:var(--vt-orange)] mb-1">{isNew ? "New service" : "Edit service"}</div>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-5">{form.name || "Untitled"}</h2>

        <div className="grid md:grid-cols-2 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Name *</label>
            <input required className="vt-input" value={form.name} onChange={upd("name")} data-testid="service-form-name" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Slug / key</label>
            <input className="vt-input" value={form.key} onChange={upd("key")} placeholder="auto from name" data-testid="service-form-key" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Category</label>
            <input className="vt-input" value={form.category} onChange={upd("category")} placeholder="maintenance / e-bike / wheels / …" data-testid="service-form-category" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Short description</label>
            <input className="vt-input" value={form.short_description} onChange={upd("short_description")} placeholder="One-line summary shown on the workshop page" data-testid="service-form-short" />
          </div>
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Full description</label>
            <textarea className="vt-input" rows="4" value={form.description} onChange={upd("description")} data-testid="service-form-description" />
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-3 mb-4">
          <div className="md:col-span-2">
            <label className="overline text-neutral-600 block mb-1">Price (€)</label>
            <input required type="number" min="0" step="1" className="vt-input" value={form.price} onChange={upd("price")} data-testid="service-form-price" />
          </div>
          <div className="md:col-span-2 flex items-end">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.checked })} data-testid="service-form-price-from" />
              Show as "from €X" (ab)
            </label>
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Duration</label>
            <input className="vt-input" value={form.duration} onChange={upd("duration")} placeholder="~1h" data-testid="service-form-duration" />
          </div>
          <div>
            <label className="overline text-neutral-600 block mb-1">Icon</label>
            <select className="vt-input" value={form.icon} onChange={upd("icon")} data-testid="service-form-icon">
              {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-4">
          <label className="overline text-neutral-600 block mb-1">Optional image</label>
          <div className="flex items-start gap-3">
            {form.image && <img src={form.image} alt="" className="w-24 h-24 object-cover border border-neutral-200" />}
            <div className="flex-1">
              <div className="flex gap-2 mb-2">
                <button type="button" onClick={() => fileInput.current?.click()} disabled={uploading}
                  className="text-xs border border-neutral-300 hover:border-black px-3 py-2 uppercase font-bold tracking-widest" data-testid="service-upload-btn">
                  <UploadIcon size={12} className="inline mr-1" /> {uploading ? "Uploading…" : "Upload"}
                </button>
                <input ref={fileInput} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} />
                {form.image && (
                  <button type="button" onClick={() => setForm({ ...form, image: "" })}
                    className="text-xs border border-neutral-300 hover:border-red-500 hover:text-red-600 px-3 py-2 uppercase font-bold tracking-widest">
                    Clear
                  </button>
                )}
              </div>
              <input className="vt-input text-xs" value={form.image} onChange={upd("image")} placeholder="or paste image URL" />
            </div>
          </div>
        </div>

        <div className="flex gap-4 mb-4 text-sm">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="service-form-active" />
            Active (visible on public workshop page)
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-200">
          <button type="button" onClick={onCancel} className="vt-btn-outline text-sm" disabled={saving}>Cancel</button>
          <button type="submit" className="vt-btn-orange text-sm" disabled={saving} data-testid="service-form-save">
            {saving ? "Saving…" : (isNew ? "Create" : "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}
